import CheckmarkAnimationUrl from '@assets/checkmark-animation.gif';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { EventTypes } from '@racemap/utilities/consts/events';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Button, Checkbox, Form, Modal } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import { IconCopy } from '../Icon';
import { Tristate } from '../utils/tristateTask';
import { Footer } from './Footer';
import { NewEventNameField } from './NewEventNameField';
import { NewEventTimesField } from './NewEventTimesField';
import { buildDuplicateEventProps } from './buildDuplicateEventProps';

type Props = {
  event: Immutable<CurrentEvent>;
};

export interface EventDuplicateFormFields {
  newEventName: string;
  newEventTimes: [DateTime, DateTime];
  withStarters: boolean;
  withGeoElements: boolean;
  copyToGroup: boolean;
}

const apiClient = RacemapAPIClient.fromWindowLocation();

export const EventDuplicateModal: FC<Props> = ({ event }) => {
  const [show, setShow] = useState(false);
  const [form] = Form.useForm<EventDuplicateFormFields>();
  const [savingState, setSavingState] = useState(Tristate.NORMAL);
  const [duplicatedEvent, setDuplicatedEvent] = useState<RacemapEvent | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const nextHourDate = DateTime.local().startOf('hour').plus({ hours: 1 });

  useEffect(() => {
    if (location.pathname.endsWith('/duplicate')) {
      setShow(true);
    }
  }, [location.pathname]);

  const handleClose = () => {
    setDuplicatedEvent(null);
    setShow(false);
    navigate(`/admin/events/${event.id}/advanced`);
  };

  const handleShow = () => {
    navigate(`/admin/events/${event.id}/advanced/duplicate`);
  };

  const handleCopyEvent = async ({
    newEventName,
    newEventTimes,
    withStarters,
    withGeoElements,
    copyToGroup,
  }: EventDuplicateFormFields) => {
    try {
      setSavingState(Tristate.SPINNING);

      setDuplicatedEvent(
        await apiClient.duplicateEvent(
          event.id,
          { withStarters, withGeoElements },
          buildDuplicateEventProps({
            event,
            newEventName,
            newEventTimes,
            copyToGroup,
          }),
        ),
      );
      setSavingState(Tristate.NORMAL);
    } catch (error) {
      toast.error(`Error duplicating event: ${(error as Error).message}`);
      setSavingState(Tristate.FAILURE);
    }
  };

  return (
    <>
      <Button icon={<IconCopy />} onClick={handleShow}>
        Duplicate Tracking Map
      </Button>

      <Modal
        open={show}
        onCancel={handleClose}
        title={
          duplicatedEvent == null ? 'Duplicate Tracking Map' : 'Event successfully duplicated.'
        }
        footer={null}
      >
        <Form<EventDuplicateFormFields>
          onFinish={handleCopyEvent}
          layout="vertical"
          form={form}
          initialValues={{
            withStarters: false,
            withGeoElements: false,
            copyToGroup: false,
            newEventTimes: [nextHourDate, nextHourDate.plus({ hours: 1 })],
            newEventName: `${event.name || 'Untitled'} (Copy)`,
          }}
        >
          {duplicatedEvent == null ? (
            <>
              <NewEventNameField oldEventName={event.name || 'Untitled'} />
              <NewEventTimesField form={form} />

              <Form.Item name="withStarters" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>With participants</Checkbox>
              </Form.Item>

              <Form.Item name="withGeoElements" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>With tracks, POIs and splits</Checkbox>
              </Form.Item>

              {(event.type === EventTypes.CONTEST || event.type === EventTypes.STAGE) && (
                <Form.Item name="copyToGroup" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>
                    Duplicate into current{' '}
                    {event.type === EventTypes.CONTEST ? 'group of single maps' : 'group of stages'}
                  </Checkbox>
                </Form.Item>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={CheckmarkAnimationUrl}
                alt="Checkmark animation"
                style={{ width: 200, height: 200 }}
              />
            </div>
          )}
          <Footer
            onClose={handleClose}
            duplicatedEvent={duplicatedEvent}
            savingState={savingState}
          />
        </Form>
      </Modal>
    </>
  );
};
